import { useEffect } from 'react';
import { trackAffiliateClick } from '../services/affiliateTrackingService';

/**
 * useReferralCookie
 *
 * Mount once at the top of the main app. On every initial render, looks for
 * `?ref=CODE` in the URL. If present, fires `POST /api/affiliate/track-click`
 * (which records the click and sets the saillab_ref cookie server-side), then
 * strips `?ref=` from the URL so the user doesn't see it.
 *
 * Notes:
 *   - The cookie is HttpOnly so we cannot read it from JS — the server is the
 *     authoritative store for attribution. We treat each page-load with a
 *     `?ref=` as a click signal; the server-side rate limiter handles dedup.
 *   - `/r/:slug` redirects are handled entirely server-side (302 + Set-Cookie),
 *     so this hook never runs against those URLs.
 *   - Strips `?ref=` via history.replaceState so the visible URL is clean
 *     after capture, but we DO NOT remove other query params.
 */
export function useReferralCookie(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const code = url.searchParams.get('ref');
    if (!code) return;

    // Trim, basic sanity (length + alphanumeric+underscore+dash). Server validates fully.
    const cleaned = code.trim();
    if (!cleaned || cleaned.length > 64 || !/^[A-Za-z0-9_-]+$/.test(cleaned)) {
      // Strip without firing — invalid code shouldn't pollute the URL
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.toString());
      return;
    }

    // Fire-and-forget. Failures are silent.
    void trackAffiliateClick(cleaned);

    // Clean the URL so the ref code isn't visible / bookmarked / shared further.
    url.searchParams.delete('ref');
    window.history.replaceState({}, '', url.toString());
    // Empty deps: run once per mount. We re-run only on full navigation
    // (which remounts the app), not on client-side route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useReferralCookie;
